/**
 * GOAL 2026-09-17 HS-1 — the HISTORICAL SCENARIO LIBRARY.
 *
 * The implementation of `docs/research/historical-scenarios/` (research version
 * 2026-09-17.1): thirteen UK windows replayed as one joint path — asset total
 * returns, CPI, RPI, wages, the savings rate and mortgage rates move together,
 * in their observed order — plus a Japanese and a US local-currency comparator
 * that are kept separate from UK history.
 *
 * A scenario is OFFERED only when four independent states all hold, and each is
 * judged separately so the reason a period is unavailable can be stated plainly:
 *
 *  1. source-complete — every observation the replay reads exists for every year
 *     (a `null` is never read as zero);
 *  2. engine-capable — the path is a UK sterling path the engine can apply
 *     (foreign local-currency panels are not: converting them needs year-end
 *     exchange rates the dataset does not hold);
 *  3. licensed — every source the replay reads is cleared for commercial use
 *     (`HISTORICAL_SOURCE_PERMISSIONS`);
 *  4. human-reviewed — a named human signed THIS version
 *     (`docs/RELEASE_SIGNOFF.md`, gate `scenario-<id>`, bound to the content
 *     fingerprint by `scripts/check-release-signoff.mjs`)
 *
 * — and the owner has released it (`offered`). A signature never transfers to a
 * changed version, and nothing here upgrades a state.
 *
 * Wording (owner decision N-C2): the client-facing `name` is a neutral pattern
 * label with the period in brackets. The research document's event headings
 * stay in `RESEARCH_WINDOWS` as adviser documentation.
 */
import { HISTORICAL_RESEARCH_VERSION, INTERNATIONAL_RECORDS, MONTHLY_RATE_QUOTES, RESEARCH_WINDOWS, UK_ANNUAL_RECORDS, } from './historical-scenario-data.js';
import { isPlaceholderPresetReviewer } from './historical-presets.js';
import { HISTORICAL_SOURCE_PERMISSIONS } from './historical-source-permissions.js';
export const SHIPPED_HISTORICAL_DATA = Object.freeze({
    uk: UK_ANNUAL_RECORDS,
    international: INTERNATIONAL_RECORDS,
    monthly: MONTHLY_RATE_QUOTES,
});
/**
 * Bumped whenever HOW figures become a replay changes (which wrappers are
 * invested, rounding, the fixed-rate reset rule, the fallback series, the tracker
 * basis, the required fields). Folded into the fingerprint, so a signature and a
 * stored replay's `contentHash` name the mapping as well as the figures.
 */
export const REPLAY_MAPPING_VERSION = '2026-09-17.1';
/**
 * Pure: the complete research extract (docs/research/historical-scenarios JSON)
 * as `HistoricalData`, with every field's source and quality flag. For tests and
 * research tooling only — the web app must never import the research files.
 */
export function historicalDataFromResearch(annual, international, monthly) {
    const fields = [
        'equity_tr_pct',
        'gilt_tr_pct',
        'house_price_pct',
        'cpi_pct',
        'rpi_pct',
        'wage_growth_pct',
        'deposit_rate_pct',
        'mortgage_rate_pct',
        'mortgage_2y75_pct',
        'bank_avg_pct',
        'bank_end_pct',
        'world_gbp_gross_pct',
    ];
    return {
        uk: annual.records.map((r) => ({
            year: r.year,
            house_price_basis: String(r['house_price_basis']),
            mortgage_basis: String(r['mortgage_basis']),
            ...Object.fromEntries(fields.map((f) => [
                f,
                { value: r[f], source: r.sources[f], quality: r.status[f] },
            ])),
        })),
        international: international.map((r) => ({ ...r, withheld: false })),
        monthly,
    };
}
/** Limits every annual replay shares, stated once and shown with every period. */
export const REPLAY_COMMON_LIMITATIONS = Object.freeze([
    'Annual calendar-year figures, applied to plan years. Movements within a year — the depth of a fall, its timing, the month of a rate change — are not shown.',
    'The historical economy is replayed onto today’s household and today’s tax rules; the tax, benefit and pension rules of the period are not recreated.',
    'Returns are index total returns before fees; the plan’s own fees are still charged. Index returns are not any fund’s results.',
    'After the last year of the period, the plan’s own assumptions resume. Nothing about later years is assumed from the period.',
]);
const V = HISTORICAL_RESEARCH_VERSION;
function freeze(value) {
    if (value !== null && typeof value === 'object' && !Object.isFrozen(value)) {
        Object.freeze(value);
        for (const v of Object.values(value))
            freeze(v);
    }
    return value;
}
const researchWindow = (id) => {
    const w = RESEARCH_WINDOWS.find((x) => x.id === id);
    if (w === undefined)
        throw new Error(`research window "${id}" missing`);
    return { from: w.from, to: w.to, bankRateStartPct: w.bankRateStartPct };
};
const uk = (entry) => {
    const w = researchWindow(entry.id);
    return {
        ...entry,
        version: V,
        kind: 'uk-replay',
        currency: 'GBP',
        country: 'GBR',
        period: { from: w.from, to: w.to },
        bankRateStartPct: w.bankRateStartPct,
    };
};
/**
 * HS-6 review (client): what a CLIENT is told about a replay, on the slide and
 * in the PDF — one sentence set, shared so the two surfaces cannot drift. The
 * adviser-facing limitations stay in the editor; this is the plain version.
 */
export const REPLAY_CLIENT_CAPTION = 'Recorded yearly UK figures for the named period — investment returns, inflation, earnings, savings and mortgage rates — replayed onto this plan using today’s tax rules. Yearly figures do not show movements within a year, returns are index returns before fees, and after the period the plan’s own assumptions resume. An illustration, not a forecast.';
const OLDER_COMPOSITES = 'Share, gilt and house-price figures are long-run research composites, not a single modern index such as the FTSE All-Share.';
const EFFECTIVE_MORTGAGE = 'Mortgage rates before 2017 are the average rate paid across all mortgages, not the rate quoted for a new deal, so a remortgage in this period is approximate.';
/**
 * The register. Every entry starts unsigned and not offered: the research
 * vintage is new, so no earlier signature can apply to it.
 */
export const HISTORICAL_SCENARIOS = freeze([
    uk({
        id: 'depression-1929',
        name: 'Prices fall and share values swing (shaped on 1929–1934)',
        description: 'Consumer prices fell in most years, shares fell for three years and then rose sharply, and gilts rose strongly. It shows how fixed debts and fixed incomes behave when prices fall.',
        limitations: [
            OLDER_COMPOSITES,
            'No RPI existed in these years. A plan with RPI-linked amounts needs a declared stand-in or cannot use this period.',
            EFFECTIVE_MORTGAGE,
            'This is the UK path, not the US share-market path of the same years.',
        ],
        review: {
            reviewedBy: null,
            reviewedAt: null,
            reviewedVersion: null,
            reviewedFingerprint: null,
        },
        offered: false,
    }),
    uk({
        id: 'stagflation-1973',
        name: 'High inflation with a deep share fall and a large rebound (shaped on 1973–1978)',
        description: 'Shares fell 28% and then 50% before rising about 150% in 1975, while prices rose by up to 23% a year. It shows withdrawals taken during the fall and the loss of purchasing power on fixed incomes.',
        limitations: [
            OLDER_COMPOSITES,
            'A rise later in the period does not restore units sold earlier; the order of the years is kept.',
            EFFECTIVE_MORTGAGE,
        ],
        review: {
            reviewedBy: null,
            reviewedAt: null,
            reviewedVersion: null,
            reviewedFingerprint: null,
        },
        offered: false,
    }),
    uk({
        id: 'tightening-1979',
        name: 'High inflation and high interest rates (shaped on 1979–1983)',
        description: 'Interest and savings rates were high while prices rose quickly, so strong nominal returns did not all add to purchasing power.',
        limitations: [
            OLDER_COMPOSITES,
            EFFECTIVE_MORTGAGE,
            'Rising unemployment in the period is not modelled as a job loss for this household.',
        ],
        review: {
            reviewedBy: null,
            reviewedAt: null,
            reviewedVersion: null,
            reviewedFingerprint: null,
        },
        offered: false,
    }),
    uk({
        id: 'october-1987',
        name: 'A year with a sharp one-day share fall, in annual figures (shaped on 1987–1989)',
        description: 'The October 1987 fall happened inside a year that still ended higher. In annual figures the fall does not appear, which is the point this period makes.',
        limitations: [
            OLDER_COMPOSITES,
            'The one-day fall is not modelled: monthly or daily figures are not available for it.',
            EFFECTIVE_MORTGAGE,
        ],
        review: {
            reviewedBy: null,
            reviewedAt: null,
            reviewedVersion: null,
            reviewedFingerprint: null,
        },
        offered: false,
    }),
    uk({
        id: 'housing-erm-1989',
        name: 'House prices fall while mortgage rates are high (shaped on 1989–1995)',
        description: 'House prices fell for four years while mortgage rates peaked above 15%, and shares and gilts moved differently from property.',
        limitations: [
            OLDER_COMPOSITES,
            EFFECTIVE_MORTGAGE,
            'Negative equity limits on remortgaging and forced sales are not modelled.',
        ],
        review: {
            reviewedBy: null,
            reviewedAt: null,
            reviewedVersion: null,
            reviewedFingerprint: null,
        },
        offered: false,
    }),
    uk({
        id: 'bonds-1994',
        name: 'Gilts and shares fall in the same year (shaped on 1994–1996)',
        description: 'Gilts fell more than shares in 1994, then both rose. It shows a portfolio holding more gilts than shares.',
        limitations: [
            OLDER_COMPOSITES,
            'One gilt series is used for all gilt holdings; short, long and index-linked gilts are not separated.',
            EFFECTIVE_MORTGAGE,
        ],
        review: {
            reviewedBy: null,
            reviewedAt: null,
            reviewedVersion: null,
            reviewedFingerprint: null,
        },
        offered: false,
    }),
    uk({
        id: 'asia-ltcm-1997',
        name: 'Overseas market stress, as seen in UK assets (shaped on 1997–1999)',
        description: 'Overseas markets were under strain while UK annual returns stayed mostly positive. Only the UK assets are replayed.',
        limitations: [
            OLDER_COMPOSITES,
            'Overseas, emerging-market or leveraged holdings are not modelled by this UK path.',
            EFFECTIVE_MORTGAGE,
        ],
        review: {
            reviewedBy: null,
            reviewedAt: null,
            reviewedVersion: null,
            reviewedFingerprint: null,
        },
        offered: false,
    }),
    uk({
        id: 'dotcom-2000',
        name: 'Three years of share falls in a row (shaped on 2000–2005)',
        description: 'UK shares fell in 2000, 2001 and 2002 before rising, while gilts and house prices rose. It shows withdrawals taken across several weak years.',
        limitations: [
            OLDER_COMPOSITES,
            'Technology-concentrated or global portfolios are not represented by the broad UK series.',
            EFFECTIVE_MORTGAGE,
        ],
        review: {
            reviewedBy: null,
            reviewedAt: null,
            reviewedVersion: null,
            reviewedFingerprint: null,
        },
        offered: false,
    }),
    uk({
        id: 'gfc-2008',
        name: 'Shares and house prices fall as interest rates are cut (shaped on 2008–2012)',
        description: 'UK shares fell 30% and house prices 15% in 2008, gilts rose, and Bank Rate fell from 5.5% to 0.5%. The later years were uneven.',
        limitations: [
            EFFECTIVE_MORTGAGE,
            'House prices are the December-on-December UK House Price Index.',
        ],
        review: {
            reviewedBy: null,
            reviewedAt: null,
            reviewedVersion: null,
            reviewedFingerprint: null,
        },
        offered: false,
    }),
    uk({
        id: 'euro-2010',
        name: 'Uneven markets while interest rates stay low (shaped on 2010–2013)',
        description: 'Bank Rate stayed at 0.5% throughout while shares, gilts and prices moved in different directions from year to year.',
        limitations: [
            EFFECTIVE_MORTGAGE,
            'Overseas government bonds are not represented by the UK gilt series.',
        ],
        review: {
            reviewedBy: null,
            reviewedAt: null,
            reviewedVersion: null,
            reviewedFingerprint: null,
        },
        offered: false,
    }),
    uk({
        id: 'brexit-2016',
        name: 'Sterling falls and UK and overseas shares diverge (shaped on 2016–2019)',
        description: 'Sterling fell after June 2016, so overseas shares measured in pounds and UK shares moved differently; 2018 was a weak year for shares.',
        limitations: [
            'The mortgage series changes in 2017 from the average rate paid to a quoted variable rate.',
            'The referendum-day movement is not shown in annual figures.',
        ],
        review: {
            reviewedBy: null,
            reviewedAt: null,
            reviewedVersion: null,
            reviewedFingerprint: null,
        },
        offered: false,
    }),
    uk({
        id: 'pandemic-2020',
        name: 'Shares fall one year and rise the next, with rates near zero (shaped on 2020–2021)',
        description: 'UK shares ended 2020 down about 10% and rose 18% in 2021; gilts rose and then fell; Bank Rate was cut to 0.1%.',
        limitations: [
            'The spring 2020 fall was much deeper during the year than the annual figure shows.',
            'Furlough or a job loss is not modelled for this household.',
        ],
        review: {
            reviewedBy: null,
            reviewedAt: null,
            reviewedVersion: null,
            reviewedFingerprint: null,
        },
        offered: false,
    }),
    uk({
        id: 'inflation-2022',
        name: 'Inflation, rising interest rates and falling gilts (shaped on 2022–2024)',
        description: 'Gilts fell 24% in 2022 while prices rose about 9%, and mortgage and savings rates rose sharply. Slower inflation afterwards did not reverse the rise in prices.',
        limitations: [
            'The September 2022 gilt-market event is part of the 2022 annual figure, not an extra loss.',
            'A mortgage is repriced only when its fixed rate ends, at the quoted rate for that month.',
        ],
        review: {
            reviewedBy: null,
            reviewedAt: null,
            reviewedVersion: null,
            reviewedFingerprint: null,
        },
        offered: false,
    }),
    {
        id: 'japan-1990',
        name: 'Long share and property decline with falling prices, in yen (Japan, shaped on 1990–2003)',
        description: 'Japanese shares and property fell over many years and prices later fell, rather than returning quickly to their starting level.',
        limitations: [
            'Figures are in yen for Japanese assets. They are not UK history and are not applied to a UK plan.',
        ],
        review: {
            reviewedBy: null,
            reviewedAt: null,
            reviewedVersion: null,
            reviewedFingerprint: null,
        },
        offered: false,
        version: V,
        kind: 'foreign-comparator',
        currency: 'JPY',
        country: 'JPN',
        period: { from: 1990, to: 2003 },
        bankRateStartPct: null,
    },
    {
        id: 'us-1929',
        name: 'Deep share fall and falling prices, in US dollars (US, shaped on 1929–1934)',
        description: 'US shares fell far more than UK shares in the same years; it is shown so the two are not confused.',
        limitations: [
            'Figures are in US dollars for US assets. They are not UK history and are not applied to a UK plan.',
        ],
        review: {
            reviewedBy: null,
            reviewedAt: null,
            reviewedVersion: null,
            reviewedFingerprint: null,
        },
        offered: false,
        version: V,
        kind: 'foreign-comparator',
        currency: 'USD',
        country: 'USA',
        period: { from: 1929, to: 1934 },
        bankRateStartPct: null,
    },
]);
/* -------------------------------------------------------------------------- */
/* What a replay reads                                                         */
/* -------------------------------------------------------------------------- */
/** Observations every UK replay needs for every year. RPI may be absent (declared). */
export const REQUIRED_REPLAY_FIELDS = Object.freeze([
    'equity_tr_pct',
    'gilt_tr_pct',
    'house_price_pct',
    'cpi_pct',
    'wage_growth_pct',
    'deposit_rate_pct',
    'mortgage_rate_pct',
    'bank_avg_pct',
]);
/** Monthly series read for a fixed-rate reset, and the source id each resolves to. */
export const MONTHLY_SERIES_SOURCE = Object.freeze({
    IUMBV34: 'S14',
    IUMTLMV: 'S13',
    IUMB6VJ: 'S12',
});
export function ukRecordsFor(scenario, data = SHIPPED_HISTORICAL_DATA) {
    const out = [];
    for (let y = scenario.period.from; y <= scenario.period.to; y += 1) {
        const r = data.uk.find((x) => x.year === y);
        if (r === undefined)
            throw new Error(`no research record for ${y}`);
        out.push(r);
    }
    return out;
}
/**
 * The sources a replay of this scenario reads: the required fields, RPI where it
 * exists, and — only when the adviser asks for a global sleeve — the global
 * equity series. The monthly fixed-rate quotes (S14) are read for a reset.
 */
export function sourcesReadBy(scenario, opts = {}, data = SHIPPED_HISTORICAL_DATA) {
    if (scenario.kind !== 'uk-replay') {
        return [...new Set(data.international.filter(inWindow(scenario)).map((r) => r.source))];
    }
    const ids = new Set();
    const fields = [...REQUIRED_REPLAY_FIELDS, 'rpi_pct'];
    if (opts.globalEquity === true)
        fields.push('world_gbp_gross_pct');
    for (const r of ukRecordsFor(scenario, data)) {
        for (const f of fields) {
            // A withheld figure is still READ by the replay: its source counts.
            if (r[f].value !== null || r[f].quality === 'withheld_unlicensed')
                ids.add(r[f].source);
        }
    }
    ids.add(MONTHLY_SERIES_SOURCE.IUMBV34);
    return [...ids].sort();
}
const inWindow = (s) => (r) => r.country === s.country && r.year >= s.period.from && r.year <= s.period.to;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
export function isScenarioReviewed(s, data = SHIPPED_HISTORICAL_DATA) {
    const r = s.review;
    return (r.reviewedBy !== null &&
        r.reviewedAt !== null &&
        ISO_DATE.test(r.reviewedAt) &&
        !Number.isNaN(Date.parse(r.reviewedAt)) &&
        r.reviewedVersion === s.version &&
        // A signature dated before the version it names existed signs nothing that
        // version holds (tech-admin review: one shared signed-at must not backdate).
        !signedBeforeVersion(r.reviewedAt, s.version) &&
        r.reviewedFingerprint === historicalScenarioFingerprint(s, data) &&
        !isPlaceholderPresetReviewer(r.reviewedBy));
}
/** True when a version label starts with a date later than the signature date. */
export function signedBeforeVersion(signedAt, version) {
    const m = /^(\d{4}-\d{2}-\d{2})/.exec(version);
    return m !== null && signedAt < m[1];
}
const FIELD_LABEL = {
    equity_tr_pct: 'share returns',
    gilt_tr_pct: 'gilt returns',
    house_price_pct: 'house prices',
    cpi_pct: 'CPI',
    rpi_pct: 'RPI',
    wage_growth_pct: 'earnings growth',
    deposit_rate_pct: 'savings rate',
    mortgage_rate_pct: 'mortgage rate',
    mortgage_2y75_pct: 'two-year fixed mortgage rate',
    bank_avg_pct: 'Bank Rate',
    bank_end_pct: 'year-end Bank Rate',
    world_gbp_gross_pct: 'global share returns',
};
export function scenarioReadiness(scenario, permissions = HISTORICAL_SOURCE_PERMISSIONS, data = SHIPPED_HISTORICAL_DATA) {
    const reasons = [];
    let missing = [];
    let rpiMissingYears = [];
    let globalEquityAvailable = false;
    let engine = { ok: true, reason: null };
    if (scenario.kind === 'uk-replay') {
        const records = ukRecordsFor(scenario, data);
        for (const r of records) {
            for (const f of REQUIRED_REPLAY_FIELDS) {
                // Withheld (unlicensed) is a licence gap, reported as such — not a missing observation.
                if (r[f].quality === 'missing')
                    missing.push(`${r.year} ${FIELD_LABEL[f]}`);
            }
        }
        rpiMissingYears = records.filter((r) => r.rpi_pct.quality === 'missing').map((r) => r.year);
        globalEquityAvailable = records.every((r) => r.world_gbp_gross_pct.quality !== 'missing');
        // Values the engine refuses rather than clips (HS-3 bounds).
        for (const r of records) {
            const out = (v, lo, hi) => v !== null && (v < lo || v > hi);
            if (out(r.equity_tr_pct.value, -100, 1000) ||
                out(r.gilt_tr_pct.value, -100, 1000) ||
                out(r.house_price_pct.value, -100, 1000) ||
                out(r.cpi_pct.value, -20, 50) ||
                out(r.rpi_pct.value, -20, 50) ||
                out(r.wage_growth_pct.value, -20, 50) ||
                out(r.deposit_rate_pct.value, -5, 30) ||
                out(r.mortgage_rate_pct.value, 0, 50)) {
                engine = {
                    ok: false,
                    reason: `a ${r.year} figure is outside the range the engine accepts`,
                };
            }
        }
    }
    else {
        missing = [];
        engine = {
            ok: false,
            reason: `its figures are in ${scenario.currency} for ${scenario.country === 'JPN' ? 'Japanese' : 'US'} assets; applying them to a UK plan needs year-end exchange rates the dataset does not hold, so it is shown for comparison only`,
        };
    }
    const notCleared = sourcesReadBy(scenario, {}, data).filter((id) => permissions[id]?.commercialUse !== 'cleared');
    const reviewed = isScenarioReviewed(scenario, data);
    if (!engine.ok)
        reasons.push(`It cannot be applied to this plan: ${engine.reason}.`);
    if (missing.length > 0) {
        reasons.push(`Figures are missing for ${missing.join(', ')}; a missing figure is never treated as zero.`);
    }
    if (notCleared.length > 0) {
        reasons.push(`Permission to use its data in this product is not yet in place (sources ${notCleared.join(', ')}).`);
    }
    if (!reviewed)
        reasons.push(`A named reviewer has not yet signed version ${scenario.version} of its figures.`);
    if (!scenario.offered)
        reasons.push('It has not been released for use in this version.');
    const available = engine.ok && missing.length === 0 && notCleared.length === 0 && reviewed && scenario.offered;
    return {
        sourceComplete: { ok: missing.length === 0, missing },
        engineCapable: engine,
        licensed: { ok: notCleared.length === 0, notCleared },
        reviewed: {
            ok: reviewed,
            reason: reviewed ? null : `version ${scenario.version} is not signed`,
        },
        offered: scenario.offered,
        rpiMissingYears,
        globalEquityAvailable,
        available,
        reasons: available ? [] : reasons,
    };
}
/** The scenarios an adviser may apply: all four states hold and the owner released it. */
export function availableHistoricalScenarios(scenarios = HISTORICAL_SCENARIOS, permissions = HISTORICAL_SOURCE_PERMISSIONS, data = SHIPPED_HISTORICAL_DATA) {
    return scenarios.filter((s) => scenarioReadiness(s, permissions, data).available);
}
/* -------------------------------------------------------------------------- */
/* Content fingerprint                                                         */
/* -------------------------------------------------------------------------- */
function canonicalJson(value) {
    if (Array.isArray(value))
        return `[${value.map(canonicalJson).join(',')}]`;
    if (value !== null && typeof value === 'object') {
        const entries = Object.entries(value)
            .filter(([, v]) => v !== undefined)
            .sort(([x], [y]) => (x < y ? -1 : x > y ? 1 : 0));
        return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(',')}}`;
    }
    return JSON.stringify(value);
}
/** The same two-lane FNV-1a the release sign-off uses for presets and tax rules. */
function fnv(text) {
    let a = 0x811c9dc5;
    let b = 0x01000193 ^ 0x7fffffff;
    for (let i = 0; i < text.length; i += 1) {
        const c = text.charCodeAt(i);
        a = Math.imul(a ^ c, 0x01000193) >>> 0;
        b = Math.imul(b ^ ((c << 8) | (c >>> 8)), 0x01000193) >>> 0;
    }
    return `${a.toString(16).padStart(8, '0')}${b.toString(16).padStart(8, '0')}`;
}
/**
 * Fingerprint of everything a signature on a scenario covers: its definition
 * and wording (not the review or the release flag) and EVERY observation its
 * replay can read — the annual records with their sources and quality flags,
 * and the monthly fixed-rate quotes for the window. Moving any figure makes a
 * signature stale; the same fingerprint is stored with every replay built from
 * it, so a later revision cannot silently change what a past run meant.
 */
export function historicalScenarioFingerprint(scenario, source = SHIPPED_HISTORICAL_DATA) {
    const { review: _review, offered: _offered, ...definition } = scenario;
    const data = scenario.kind === 'uk-replay'
        ? {
            annual: ukRecordsFor(scenario, source),
            monthly: Object.fromEntries(Object.entries(source.monthly).map(([code, series]) => [
                code,
                Object.fromEntries(Object.entries(series).filter(([d]) => {
                    const y = Number(d.slice(0, 4));
                    return y >= scenario.period.from && y <= scenario.period.to;
                })),
            ])),
        }
        : { international: source.international.filter(inWindow(scenario)) };
    return fnv(canonicalJson({
        definition,
        data,
        common: REPLAY_COMMON_LIMITATIONS,
        mapping: REPLAY_MAPPING_VERSION,
        required: REQUIRED_REPLAY_FIELDS,
    }));
}
//# sourceMappingURL=historical-scenarios.js.map